import { createContext, useContext, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import { _setTenantScopeGetter } from '../lib/api.js';
import { useAuth, type AuthState } from './AuthContext.js';
import type { SiteMode } from '../lib/setupStatus.js';

interface TenantScope {
  tenantId: string | null;
  tenantName: string | null;
  pinned: boolean;
}

const Ctx = createContext<TenantScope | null>(null);

function derivedTenantId(state: AuthState, siteMode: SiteMode): string | null {
  if (state.status !== 'authed') return null;
  if (state.user.tenant_id) return state.user.tenant_id;
  if (state.user.role !== 'master_admin') return null;
  if (siteMode !== 'single_tenant' && siteMode !== 'multi_family') return null;
  return state.me.default_tenant_id;
}

function derivedHeaderTenantId(state: AuthState, siteMode: SiteMode): string | null {
  if (state.status !== 'authed') return null;
  if (state.user.role !== 'master_admin' || state.user.tenant_id != null) return null;
  if (siteMode !== 'single_tenant' && siteMode !== 'multi_family') return null;
  return state.me.default_tenant_id;
}

export function TenantScopeProvider({
  children,
  siteMode,
}: PropsWithChildren<{ siteMode: SiteMode }>) {
  const { state } = useAuth();
  const tenantId = derivedTenantId(state, siteMode);
  const tenantName = state.status === 'authed' ? (state.me.tenant?.name ?? null) : null;
  const pinned =
    state.status === 'authed' &&
    state.user.tenant_id == null &&
    state.me.default_tenant_id !== null &&
    state.me.default_tenant_id === tenantId;

  const scopeRef = useRef<{ state: AuthState; siteMode: SiteMode }>({ state, siteMode });
  scopeRef.current = { state, siteMode };

  useState(() => {
    _setTenantScopeGetter(() => {
      const { state: currentState, siteMode: currentMode } = scopeRef.current;
      return derivedHeaderTenantId(currentState, currentMode);
    });
    return null;
  });

  const value = useMemo<TenantScope>(
    () => ({
      tenantId,
      tenantName,
      pinned,
    }),
    [tenantId, tenantName, pinned],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTenantScope(): TenantScope {
  const v = useContext(Ctx);
  if (!v) throw new Error('TenantScopeProvider missing');
  return v;
}
